import type { ComponentType } from 'react'
import { MissionStackRolloutCard } from './MissionStackRolloutCard'
import { UIVariantRolloutCard } from './UIVariantRolloutCard'
import { IngestionOfferingCard } from './IngestionOfferingCard'
import { SettingsRolloutCard } from './SettingsRolloutCard'
import { ManagedChatAddressCard } from './ManagedChatAddressCard'

export interface RolloutControlOrganization {
  id: string
  name: string
}

export interface RolloutControlProps {
  /** The organisation the card shows and changes. */
  organization: RolloutControlOrganization
  /**
   * The signed-in user's active organisation. A card that supports a target
   * organisation uses the staff routes when `organization` differs from it.
   */
  activeOrganizationId: string
}

export interface RolloutControlDefinition {
  id: string
  Component: ComponentType<RolloutControlProps>
  /**
   * True when a platform administrator may point the card at an organisation
   * other than the active one (PLAN-20260930 slice 5, condition 7). Only cards
   * the staff rollout route serves declare it; every other card renders for
   * the active organisation only.
   */
  supportsTargetOrganization: boolean
}

// This registry is a reusable Control UI presentation seam, not a generic
// feature-flag authority. Every entry owns a typed domain API and its removal
// lifecycle; adding a card cannot create an ungoverned string/boolean flag.
export const rolloutControlCatalog: readonly RolloutControlDefinition[] = [
  // mission-stack: staff route GET/PUT staff/rollout/mission-stack/organizations/{id}
  // serves a target organisation (PLAN-20260930 slice 5).
  { id: 'mission-stack', Component: MissionStackRolloutCard, supportsTargetOrganization: true },
  // ui-variant: typed API in infra_ui_variant_rollout.service.ts (levels off,
  // default-next, next-only), governed by PLAN-20260909 (docs/plans/canonical/
  // platform-control-plane/user-interface/ui-variant-rollout/). Every member
  // of an unlocked environment uses the new interface (agion/Agion#3210); the
  // level now only gates six-level governance (next-only). Removal sequence:
  // legacy shell, legacy theme file, then the data-ui-variant attribute; this
  // entry stays while next-only gates six-level governance, and the policy
  // tables and history remain as evidence.
  // The staff route serves a target organisation (PLAN-20260930 slice 5).
  { id: 'ui-variant', Component: UIVariantRolloutCard, supportsTargetOrganization: true },
  // ingestion-offering: typed API in infra_ingestion_offering.service.ts
  // (organisation opt-out list over the host's ingestion connector
  // capability answer), governed by PLAN-20260911 (docs/plans/canonical/
  // cross-category/ingestion/connector-capability/). Removal: delete this
  // entry; the exclusion table, the offering API and the history remain.
  // No staff route: active organisation only.
  { id: 'ingestion-offering', Component: IngestionOfferingCard, supportsTargetOrganization: false },
  // settings: typed API in infra_settings_rollout.service.ts (levels off, on;
  // staff ranges of the live settings), governed by PLAN-20261013 slice 08d
  // (agion/Agion#3155). Only the staff route serves it, for the active
  // organisation too. The server registers its switch with an owner and a
  // removal date; removal deletes this entry, and the policy and history
  // tables remain as evidence.
  { id: 'settings', Component: SettingsRolloutCard, supportsTargetOrganization: true },
  // managed-chat-address: typed API in infra_managed_chat_rollout.service.ts
  // (levels off, on; the organisation's address limit; Agion's Cloudflare
  // key health), governed by PLAN-20261015 slice 15 (agion/Agion#3473,
  // ADR-003 G1, G7, G15). Only the staff route serves it, for the active
  // organisation too.
  { id: 'managed-chat-address', Component: ManagedChatAddressCard, supportsTargetOrganization: true },
]

/** The catalogue entries that render for the chosen organisation. */
export function rolloutControlsFor(crossOrganization: boolean): readonly RolloutControlDefinition[] {
  return crossOrganization
    ? rolloutControlCatalog.filter((control) => control.supportsTargetOrganization)
    : rolloutControlCatalog
}
